import React, { forwardRef } from 'react';
import { object, node, func, bool, string } from 'prop-types';
import { translate } from '@airasia-common/libraries/i18n/LocaleProvider';
import { EMPTY_FUNCTION } from '@airasia-common/libraries/constants';

import Wrapper from '../Wrapper';
import Row from '../Container/Row';
import ShowMore from '../ShowMore';
import Divider from '../Divider';
import { H3 } from '../Text/Heading';

import {
  CheckinIcon,
  CheckoutIcon,
  DollarIcon,
  SmileyIcon,
} from './DescriptionIcons';
import ChildPolicies from './ChildPolicies';
import DescriptionItem from './DescriptionItem';

DescriptionItem.propTypes = {
  label: node,
  content: node,
};

const DescriptionAndPolicies = ({
  description = {},
  checkinDetails = {},
  checkoutDetails = {},
  toggleClickShowMore,
  toggleClickShowMorePolicies,
  policies = {},
  fees = {},
  onDescriptionExpand,
  onPolicyExpand,
  clickShowMore,
  clickShowMorePolicies,
  hotelDescriptionOpeningRef,
  hotelDescriptionClosingRef,
  hotelPoliciesOpeningRef,
  theme,
  move,
}) => {
  const {
    dining,
    business_amenities,
    rooms,
    attractions,
    nearestAirports,
    location,
  } = description;
  const { age_policy = {}, know_before_you_go = '', childPolicies } = policies;
  const { children_stay_free: childrenStayFree } = age_policy;
  const { mandatory, optional } = fees;
  const { instructions = '', special_instructions = '' } = checkinDetails;

  const hasDescriptionInfo = !!(
    dining ||
    business_amenities ||
    rooms ||
    attractions ||
    nearestAirports ||
    location
  );

  return (
    <>
      {hasDescriptionInfo && (
        <>
          <Wrapper
            ref={hotelDescriptionOpeningRef}
            style={{ position: 'relative', top: -120 }}
          />
          <H3
            id="hotel-descriptions-title"
            lineHeight="26px"
            marginBottom="2px"
            marginTop="30px"
            fontSize={['16px', '20px']}
          >
            {translate('common.hotelDescription')}
          </H3>
          <ShowMore
            move={move}
            theme={theme}
            clickShowMore={clickShowMore}
            id="hotel-descriptions-panel"
            visibleContent={
              <DescriptionItem
                label={translate('common.amenities')}
                content={description.amenities}
                className="amenity-descriptions"
              />
            }
            collapseContent={
              <>
                {dining && (
                  <DescriptionItem
                    label={translate('common.dining')}
                    content={dining}
                    className="dining-descriptions"
                  />
                )}
                {business_amenities && (
                  <DescriptionItem
                    label={translate('common.businessAmenities')}
                    content={business_amenities}
                    className="business-amenities-descriptions"
                  />
                )}
                {rooms && (
                  <DescriptionItem
                    label={translate('common.roomsDescription')}
                    content={rooms}
                    className="room-descriptions"
                  />
                )}
                {attractions && (
                  <DescriptionItem
                    label={translate('common.attractions')}
                    content={attractions}
                    className="attraction-descriptions"
                  />
                )}
                {nearestAirports && (
                  <DescriptionItem
                    label={translate('common.nearestAirports')}
                    content={nearestAirports}
                    className="nearest-airport"
                  />
                )}
                {location && (
                  <DescriptionItem
                    label={translate('common.location')}
                    content={location}
                    className="location-descriptions"
                  />
                )}
              </>
            }
            onToggle={isCollapsed => {
              toggleClickShowMore(!isCollapsed);
              if (!isCollapsed) {
                onDescriptionExpand();
              }
            }}
          />
          <Wrapper
            ref={hotelDescriptionClosingRef}
            style={{ position: 'relative', top: -120 }}
          />
        </>
      )}
      {hasDescriptionInfo && <Divider marginY="16px" />}
      <>
        <Wrapper
          ref={hotelPoliciesOpeningRef}
          style={{ position: 'relative', top: -120 }}
        />
        <H3
          id="hotel-policy-title"
          lineHeight="26px"
          marginBottom="9px"
          marginTop={hasDescriptionInfo ? '16px' : '30px'}
          fontSize={['16px', '20px']}
        >
          {translate('common.hotelPolicies')}
        </H3>
        <ShowMore
          move={move}
          theme={theme}
          clickShowMore={clickShowMorePolicies}
          id="hotel-policy-panel"
          visibleContent={
            <Wrapper>
              <DescriptionItem
                label={
                  <>
                    <Row className="checkin" alignItems="center">
                      <CheckinIcon icon="InputIcon" fontsize="normal" />
                      {translate('common.checkIn')}: {checkinDetails.time}
                    </Row>
                    <Row className="checkout" alignItems="center">
                      <CheckoutIcon icon="InputIcon" fontsize="normal" />
                      {translate('common.checkOut')}: {checkoutDetails.time}
                    </Row>
                  </>
                }
                className="checkin-checkout-policy"
              />
              <Wrapper>
                {instructions.length > 0 && (
                  <DescriptionItem
                    content={instructions}
                    className="instructions"
                  />
                )}
                {special_instructions.length > 0 && (
                  <DescriptionItem
                    label={translate('common.specialInstructions')}
                    content={special_instructions}
                    className="special-instructions"
                  />
                )}
                {Boolean(age_policy?.min_adult_age) && (
                  <DescriptionItem
                    label={`${translate('common.minimumAge')}: ${
                      age_policy.min_adult_age
                    }`}
                    className="minimum-age-policy"
                  />
                )}
                {childPolicies && (
                  <ChildPolicies
                    childPolicies={childPolicies}
                    childrenStayFree={childrenStayFree}
                  ></ChildPolicies>
                )}
              </Wrapper>
            </Wrapper>
          }
          collapseContent={
            mandatory || optional || know_before_you_go.length > 0 ? (
              <>
                {(mandatory || optional) && (
                  <>
                    <DescriptionItem
                      label={
                        <Row
                          className="fees"
                          alignItems="center"
                          marginTop="9px"
                        >
                          <DollarIcon
                            icon="MonetizationOnIcon"
                            fontsize="titleSmall"
                          />
                          {translate('common.fees')}
                        </Row>
                      }
                    />
                    <Wrapper paddingLeft="21.5px">
                      {mandatory && (
                        <DescriptionItem
                          label={translate('common.mandatory')}
                          content={mandatory}
                          className="mandatory"
                        />
                      )}
                      {optional && (
                        <DescriptionItem
                          label={translate('common.optional')}
                          content={optional}
                          className="optional"
                        />
                      )}
                    </Wrapper>
                  </>
                )}
                {know_before_you_go && (
                  <>
                    <DescriptionItem
                      label={
                        <Row alignItems="center" marginTop="9px">
                          <SmileyIcon
                            icon="InsertEmoticon"
                            fontsize="titleSmall"
                          />
                          {translate('common.knowBeforeYouGo')}
                        </Row>
                      }
                      className="knowledge-title"
                    />
                    <Wrapper paddingLeft="21.5px">
                      <DescriptionItem
                        content={know_before_you_go}
                        className="knowledge"
                      />
                    </Wrapper>
                  </>
                )}
              </>
            ) : null
          }
          onToggle={isCollapsed => {
            toggleClickShowMorePolicies(!isCollapsed);
            if (!isCollapsed) {
              onPolicyExpand();
            }
          }}
        />
      </>
    </>
  );
};

DescriptionAndPolicies.propTypes = {
  description: object,
  checkinDetails: object,
  checkoutDetails: object,
  policies: object,
  fees: object,
  onDescriptionExpand: func,
  onPolicyExpand: func,
  clickShowMore: bool,
  toggleClickShowMore: func,
  clickShowMorePolicies: bool,
  toggleClickShowMorePolicies: func,
  hotelDescriptionOpeningRef: object,
  hotelDescriptionClosingRef: object,
  hotelPoliciesOpeningRef: object,
  theme: string,
  move: bool,
};

DescriptionAndPolicies.defaultProps = {
  onDescriptionExpand: EMPTY_FUNCTION,
  onPolicyExpand: EMPTY_FUNCTION,
  toggleClickShowMorePolicies: EMPTY_FUNCTION,
  clickShowMore: false,
  clickShowMorePolicies: false,
  theme: 'AIRASIA_CLASSIC',
  move: false,
};

export default forwardRef(DescriptionAndPolicies);
